import { batch } from 'react-redux';
import { format } from 'date-fns';
import download from 'downloadjs';

import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastManager';
import { axiosAuthed } from '../../../shared/utils/setCommonHeaders';
import { authProvider } from '../../login/ducks/loginActions';

import {
  CLOSE_BOARD,
  FETCH_EXERCISES_START,
  FETCH_EXERCISES_FULFILLED,
  FETCH_EXERCISES_REJECTED,
  FETCH_GROUPS_START,
  FETCH_GROUPS_FULFILLED,
  FETCH_GROUPS_REJECTED,
  FETCH_LEADERBOARD_START,
  FETCH_LEADERBOARD_FULFILLED,
  FETCH_LEADERBOARD_REJECTED,
  EDIT_LEADERBOARD_START,
  EDIT_LEADERBOARD_FULFILLED,
  FETCH_LEADERBOARD_CSV_START,
  FETCH_LEADERBOARD_CSV_FULFILLED,
  FETCH_NEXT_PAGE_START,
  FETCH_NEXT_PAGE_FULFILLED,
  UPDATE_SETTING,
  SET_MODAL_OPEN,
  FETCH_TEMPLATES_START,
  FETCH_TEMPLATES_FULFILLED,
  FETCH_TEMPLATES_REJECTED,
  SAVE_TEMPLATE_START,
  SAVE_TEMPLATE_FULFILLED,
  SAVE_TEMPLATE_REJECTED,
  UPDATE_TEMPLATE_START,
  UPDATE_TEMPLATE_FULFILLED,
  UPDATE_TEMPLATE_REJECTED,
  DELETE_TEMPLATE_START,
  DELETE_TEMPLATE_FULFILLED,
  DELETE_TEMPLATE_REJECTED,
  MARK_BOARD_AS_TEMPLATE,
  SET_LEADERBOARD_VIEW,
  SET_ACTIVE_FILTERS,
  CLEAR_ALL_BOARDS,
} from './actionTypes';

authProvider();

export const fetchExercises = (accountCode, exerciseType) => (
  (dispatch) => {
    dispatch({ type: FETCH_EXERCISES_START });
    axiosAuthed.get(`/accounts/${accountCode}/exercises?type=${exerciseType}&tracked=1`)
      .then((response) => {
        dispatch({
          type: FETCH_EXERCISES_FULFILLED,
          payload: response.data,
        });
      })
      .catch((err) => {
        dispatch({
          type: FETCH_EXERCISES_REJECTED,
          payload: err,
        });
      });
  }
);

export const fetchGroups = (accountCode) => (
  (dispatch) => {
    dispatch({ type: FETCH_GROUPS_START });
    axiosAuthed.get(`/accounts/${accountCode}/groups?leaderboard=1`)
      .then((response) => {
        dispatch({
          type: FETCH_GROUPS_FULFILLED,
          payload: response.data,
        });
      })
      .catch((err) => {
        dispatch({
          type: FETCH_GROUPS_REJECTED,
          payload: err,
        });
        if (err?.response?.status !== 403) {
          showErrorToast(err?.response?.headers?.message || 'Failed to load groups. Please try again.');
        }
      });
  }
);

export const fetchLeaderboard = (values, actionType, id, isTemplate = false, templateId = null) => (
  (dispatch, getState) => {
    const {
      accountCode,
      compareTo,
      compareDates,
      exerciseId,
      exerciseName,
      gender,
      groups,
      mustBeInAllGroups,
      rangeType,
      rangeDates,
    } = values;

    const currentPage = actionType !== 'loadMore' ? 1 : getState().leaderboard.data.leaderboards[id].page;
    const page = actionType !== 'loadMore' ? 1 : currentPage + 1;

    const accountString = `/accounts/${accountCode}`;
    const exerciseQuery = `/exercises/${exerciseId}/`;
    const leaderboardQuery = `leaderboard?page=${page}&pageSize=50`;
    const genderQuery = `${gender ? `&gender=${gender}` : ''}`;
    const groupsQuery = `${groups.map((group) => `&groupIds[]=${group.id}`).join('')}`;
    const allGroups = `${groups.length !== 0 ? `&mustBeInAllGroups=${mustBeInAllGroups}` : ''}`;
    const rangeQuery = `${rangeType ? (`&rangeType=${rangeType}`) : ''}`;
    const rangeDatesQuery = (rangeDates && rangeType === 4 && rangeDates.startDate && rangeDates.endDate)
      ? `&rangeMinDate=${format(new Date(rangeDates.startDate), 'yyyy-MM-dd')}&rangeMaxDate=${format(new Date(rangeDates.endDate), 'yyyy-MM-dd')}`
      : '';
    const compareToQuery = `${compareTo ? (`&compareTo=${compareTo}`) : ''}`;
    const compareDatesQuery = (compareDates && compareTo === 4 && compareDates.startDate && compareDates.endDate)
      ? `&compareMinDate=${format(new Date(compareDates.startDate), 'yyyy-MM-dd')}&compareMaxDate=${format(new Date(compareDates.endDate), 'yyyy-MM-dd')}`
      : '';
    const downloadQuery = `${actionType === 'download' ? '&format=csv' : ''}`;

    // Default to 0 for 'add' (no existing board); callers pass undefined in that case.
    const resolvedId = id ?? 0;

    if (actionType === 'add') {
      dispatch({ type: FETCH_LEADERBOARD_START });
    }
    if (actionType === 'edit') {
      dispatch({ type: EDIT_LEADERBOARD_START });
    }
    if (actionType === 'download') {
      dispatch({ type: FETCH_LEADERBOARD_CSV_START });
    }
    if (actionType === 'loadMore') {
      dispatch({
        type: FETCH_NEXT_PAGE_START,
        payload: {
          id: resolvedId,
        },
      });
    }

    return axiosAuthed.get(`${accountString}${exerciseQuery}${leaderboardQuery}${genderQuery}${groupsQuery}${allGroups}${rangeQuery}${rangeDatesQuery}${compareToQuery}${compareDatesQuery}${downloadQuery}`)
      .then((response) => {
        if (actionType === 'add') {
          return new Promise((resolve) => {
            setTimeout(() => {
              batch(() => {
                dispatch({
                  type: FETCH_LEADERBOARD_FULFILLED,
                  payload: {
                    data: response.data,
                    exerciseName,
                    values,
                    isTemplate,
                    templateId,
                  },
                });
                dispatch({
                  type: SET_MODAL_OPEN,
                  payload: {
                    isModalOpen: false,
                  },
                });
              });
              resolve();
            }, 800);
          });
        }
        if (actionType === 'edit') {
          return new Promise((resolve) => {
            setTimeout(() => {
              batch(() => {
                dispatch({
                  type: EDIT_LEADERBOARD_FULFILLED,
                  payload: {
                    id: resolvedId,
                    data: response.data,
                    exerciseName,
                    values,
                  },
                });
                dispatch({
                  type: SET_MODAL_OPEN,
                  payload: {
                    isModalOpen: false,
                  },
                });
              });
              resolve();
            }, 800);
          });
        }
        if (actionType === 'download') {
          dispatch({
            type: FETCH_LEADERBOARD_CSV_FULFILLED,
            payload: response.data,
          });
          download(response.data, `Teambuildr ${exerciseName} Leaderboard.csv`, 'text/csv');
        }
        if (actionType === 'loadMore') {
          if (currentPage !== page) {
            setTimeout(() => {
              dispatch({
                type: FETCH_NEXT_PAGE_FULFILLED,
                payload: {
                  id: resolvedId,
                  data: response.data,
                  exerciseName,
                  page: response.data.length === 0 ? currentPage : page,
                },
              });
            }, 800);
          }
        }
        return null;
      })
      .catch((err) => {
        dispatch({ type: FETCH_LEADERBOARD_REJECTED, payload: err });
        if (err.response?.status === 403) {
          showErrorToast('You no longer have access to this leaderboard.');
          // eslint-disable-next-line no-use-before-define
          dispatch(fetchTemplates());
          if (actionType !== 'add') {
            dispatch({ type: CLOSE_BOARD, payload: { id: resolvedId } });
          }
          const remaining = Object.keys(getState().leaderboard.data.leaderboardsReference);
          if (remaining.length === 0) {
            dispatch({ type: SET_LEADERBOARD_VIEW, payload: { view: 'home', editingTemplateId: null } });
          }
          return; // 403 fully handled
        }
        if (actionType === 'download') {
          showErrorToast(err?.response?.headers?.message || 'Failed to export leaderboard. Please try again.');
          return; // modal already closed, toast is sufficient
        }
        throw err; // propagate to caller for in-modal/in-form error handling
      });
  }
);

export const closeBoard = (id) => (
  (dispatch) => {
    dispatch({ type: CLOSE_BOARD, payload: { id } });
  }
);

export const updateSetting = (id, key, value) => ({
  type: UPDATE_SETTING,
  payload: {
    id,
    setting: key,
    value,
  },
});

export const setModalOpen = (isModalOpen, activeModal, activeBoardId) => ({
  type: SET_MODAL_OPEN,
  payload: {
    isModalOpen,
    activeModal,
    activeBoardId: !activeBoardId ? 0 : activeBoardId,
  },
});

// Converts an API template response to our internal Redux shape.
// Groups are resolved from the Redux store against the returned groupIds.
const exerciseTypeMap = { lift: 'L', saqc: 'R', circuit: 'C' };

const genderToNumber = {
  All: 0, Male: 1, Female: 2, Undefined: 3,
};

const rangeTypeToNumber = {
  AllTime: 1, ThirtyDays: 2, Today: 3, Custom: 4,
};

const compareToToNumber = {
  AllTime: 1,
  ThirtyDays: 2,
  Today: 3,
  Custom: 4,
};

const normalizeTemplate = (apiTemplate) => {
  const hasCustomRange = apiTemplate.rangeType === 'Custom'
    && apiTemplate.rangeMinDate && apiTemplate.rangeMaxDate;
  // Append T00:00:00 so the string is parsed as local midnight, not UTC midnight.
  // new Date('YYYY-MM-DD') follows ISO spec and gives UTC midnight, which shifts
  // the displayed date by one day in UTC- timezones.
  const localDate = (str) => new Date(`${str}T00:00:00`);
  const rangeDates = hasCustomRange
    ? { startDate: localDate(apiTemplate.rangeMinDate), endDate: localDate(apiTemplate.rangeMaxDate) }
    : {};

  const hasCustomCompare = apiTemplate.compareTo === 'Custom'
    && apiTemplate.compareToMinDate && apiTemplate.compareToMaxDate;
  const compareDates = hasCustomCompare
    ? {
      startDate: localDate(apiTemplate.compareToMinDate),
      endDate: localDate(apiTemplate.compareToMaxDate),
    }
    : {};

  return {
    id: apiTemplate.id,
    createdAt: apiTemplate.createdAt,
    category: exerciseTypeMap[apiTemplate.exercise?.type] || apiTemplate.exercise?.type,
    exerciseId: apiTemplate.exercise?.id,
    exerciseName: apiTemplate.exercise?.name,
    groups: apiTemplate.groups || [],
    mustBeInAllGroups: apiTemplate.mustBeInAllGroups,
    gender: genderToNumber[apiTemplate.gender] ?? 0,
    rangeType: rangeTypeToNumber[apiTemplate.rangeType] || 0,
    rangeDates,
    compareTo: compareToToNumber[apiTemplate.compareTo] ?? 0,
    compareDates,
  };
};

// Converts our internal Formik shape to the API request payload.
const denormalizeTemplate = (formValues) => {
  const {
    exerciseId,
    groups,
    mustBeInAllGroups,
    gender,
    rangeType,
    rangeDates,
    compareTo,
    compareDates,
  } = formValues;

  const rangeMinDate = (rangeType === 4 && rangeDates?.startDate)
    ? format(new Date(rangeDates.startDate), 'yyyy-MM-dd') : null;
  const rangeMaxDate = (rangeType === 4 && rangeDates?.endDate)
    ? format(new Date(rangeDates.endDate), 'yyyy-MM-dd') : null;
  const compareMinDate = (compareTo === 4 && compareDates?.startDate)
    ? format(new Date(compareDates.startDate), 'yyyy-MM-dd') : null;
  const compareMaxDate = (compareTo === 4 && compareDates?.endDate)
    ? format(new Date(compareDates.endDate), 'yyyy-MM-dd') : null;

  return {
    exerciseId,
    groupIds: (groups || []).map((g) => g.id),
    mustBeInAllGroups,
    gender,
    rangeType,
    rangeMinDate,
    rangeMaxDate,
    compareTo: compareTo || null,
    compareMinDate,
    compareMaxDate,
  };
};

export const fetchTemplates = () => (dispatch, getState) => {
  const { accountCode } = getState().auth.data.currentUser;
  dispatch({ type: FETCH_TEMPLATES_START });
  axiosAuthed.get(`/accounts/${accountCode}/leaderboards/templates`)
    .then((response) => {
      dispatch({
        type: FETCH_TEMPLATES_FULFILLED,
        payload: response.data.map((t) => normalizeTemplate(t)),
      });
    })
    .catch((err) => {
      dispatch({ type: FETCH_TEMPLATES_REJECTED, payload: err });
      showErrorToast(err?.response?.headers?.message || 'Failed to load leaderboard templates. Please try again.');
    });
};

export const markBoardAsTemplate = (boardId, templateId) => ({
  type: MARK_BOARD_AS_TEMPLATE,
  payload: { boardId, templateId },
});

export const saveTemplate = (formValues, boardId = null) => (dispatch, getState) => {
  const { accountCode } = getState().auth.data.currentUser;
  dispatch({ type: SAVE_TEMPLATE_START });
  return axiosAuthed.post(`/accounts/${accountCode}/leaderboards/templates`, denormalizeTemplate(formValues))
    .then((response) => {
      const normalized = normalizeTemplate(response.data);
      dispatch({ type: SAVE_TEMPLATE_FULFILLED, payload: normalized });
      if (boardId !== null) {
        dispatch(markBoardAsTemplate(boardId, normalized.id));
      }
      showSuccessToast('Leaderboard template saved successfully!');
      return normalized;
    })
    .catch((err) => {
      dispatch({ type: SAVE_TEMPLATE_REJECTED, payload: err });
      if (err.response?.status === 403) {
        showErrorToast('You no longer have access to create a template with the selected groups.');
        dispatch(fetchGroups(accountCode));
        return; // 403 fully handled
      }
      throw err;
    });
};

export const updateTemplate = (templateId, formValues, boardId = null) => (dispatch, getState) => {
  const { accountCode } = getState().auth.data.currentUser;
  dispatch({ type: UPDATE_TEMPLATE_START });
  return axiosAuthed.put(
    `/accounts/${accountCode}/leaderboards/templates/${templateId}`,
    denormalizeTemplate(formValues),
  )
    .then((response) => {
      dispatch({
        type: UPDATE_TEMPLATE_FULFILLED,
        payload: normalizeTemplate(response.data),
      });
      showSuccessToast('Leaderboard template updated successfully!');
    })
    .catch((err) => {
      dispatch({ type: UPDATE_TEMPLATE_REJECTED, payload: err });
      if (err.response?.status === 403) {
        showErrorToast('You no longer have access to edit this template.');
        dispatch(fetchTemplates());
        if (boardId !== null) {
          dispatch({ type: CLOSE_BOARD, payload: { id: boardId } });
          const remaining = Object.keys(getState().leaderboard.data.leaderboardsReference);
          if (remaining.length === 0) {
            dispatch({ type: SET_LEADERBOARD_VIEW, payload: { view: 'home', editingTemplateId: null } });
          }
        }
        return; // 403 fully handled
      }
      throw err;
    });
};

export const deleteTemplate = (templateId) => (dispatch, getState) => {
  const { accountCode } = getState().auth.data.currentUser;
  dispatch({ type: DELETE_TEMPLATE_START });
  axiosAuthed.delete(`/accounts/${accountCode}/leaderboards/templates/${templateId}`)
    .then(() => {
      dispatch({ type: DELETE_TEMPLATE_FULFILLED, payload: templateId });
      showSuccessToast('Leaderboard template deleted successfully!');
    })
    .catch((err) => {
      if (err.response?.status === 403) {
        showErrorToast('You no longer have access to delete this template.');
        dispatch(fetchTemplates());
      } else {
        showErrorToast(err?.response?.headers?.message || 'Failed to delete template. Please try again.');
      }
      dispatch({ type: DELETE_TEMPLATE_REJECTED, payload: err });
    });
};

export const setLeaderboardView = (view, editingTemplateId = null) => ({
  type: SET_LEADERBOARD_VIEW,
  payload: { view, editingTemplateId },
});

export const clearAllBoards = () => ({ type: CLEAR_ALL_BOARDS });

export const fetchAllExercises = (accountCode) => (
  (dispatch) => {
    dispatch({ type: FETCH_EXERCISES_START });
    axiosAuthed.get(`/accounts/${accountCode}/exercises?tracked=1`)
      .then((response) => {
        dispatch({
          type: FETCH_EXERCISES_FULFILLED,
          payload: response.data,
        });
      })
      .catch((err) => {
        dispatch({
          type: FETCH_EXERCISES_REJECTED,
          payload: err,
        });
      });
  }
);

export const setActiveFilters = (filters) => ({
  type: SET_ACTIVE_FILTERS,
  payload: filters,
});
